import { useState } from "react";
import { motion } from "framer-motion";
import { Check, X, Sparkles, ExternalLink, Download, Gavel } from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, LineChart, Line,
  PieChart, Pie, Cell, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";

const PALETTE = ["#6C5CE7", "#C9A96A", "#10B981", "#F43F5E", "#0EA5E9", "#0E1A2B"];
const TONES = {
  violet: "from-[#6C5CE7]/15 to-[#6C5CE7]/5 text-[#5a49d6] border-[#6C5CE7]/20",
  emerald: "from-emerald-500/15 to-emerald-500/5 text-emerald-700 border-emerald-500/20",
  gold: "from-[#C9A96A]/20 to-[#C9A96A]/5 text-[#8a6d34] border-[#C9A96A]/30",
  rose: "from-rose-500/15 to-rose-500/5 text-rose-700 border-rose-500/20",
  navy: "from-navy/10 to-navy/5 text-navy border-navy/15",
};

const ease = [0.16, 1, 0.3, 1];

function KpisBlock({ block }) {
  return (
    <div data-testid="ai-block-kpis" className="my-3">
      {block.title && <div className="overline text-navy/40 mb-2">{block.title}</div>}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {(block.cards || []).map((c, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: i * 0.08, ease }}
            className={`rounded-2xl border bg-gradient-to-br p-4 ${TONES[c.tone] || TONES.navy}`}>
            <div className="text-[11px] uppercase tracking-widest opacity-70">{c.label}</div>
            <div className="mt-1.5 text-2xl font-medium tracking-tight tabular-nums">{c.value}</div>
            {c.hint && <div className="mt-1 text-[11px] opacity-60">{c.hint}</div>}
          </motion.div>
        ))}
      </div>
    </div>
  );
}

function TableBlock({ block }) {
  const cols = block.columns || [];
  const rows = block.rows || [];
  return (
    <motion.div data-testid="ai-block-table" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease }} className="my-3 rounded-2xl border border-navy/10 bg-white overflow-hidden shadow-sm">
      {block.title && (
        <div className="px-5 pt-4 pb-2 flex items-center gap-2">
          <span className="w-1.5 h-1.5 rounded-full bg-[#6C5CE7]" />
          <span className="text-sm font-medium text-navy">{block.title}</span>
          <span className="ml-auto text-[11px] text-navy/40">{rows.length} registros</span>
        </div>
      )}
      <div className="overflow-x-auto max-h-96 thin-scroll">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-cream/95 backdrop-blur">
            <tr>
              {cols.map((c) => (
                <th key={c.key} className="text-left px-5 py-2.5 text-[11px] uppercase tracking-wider text-navy/45 font-medium whitespace-nowrap">{c.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-t border-navy/5 hover:bg-[#6C5CE7]/[0.04] transition-colors">
                {cols.map((c) => (
                  <td key={c.key} className="px-5 py-2.5 text-navy/80 whitespace-nowrap max-w-[260px] truncate">
                    {String(r[c.key] ?? "—")}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </motion.div>
  );
}

function GlassTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-xl border border-navy/10 bg-white/95 backdrop-blur-xl shadow-xl shadow-navy/10 px-3.5 py-2.5">
      {label != null && <div className="text-[11px] uppercase tracking-wider text-navy/40 mb-1">{label}</div>}
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2 text-sm">
          <span className="w-2 h-2 rounded-full" style={{ background: p.color || p.payload?.fill || "#6C5CE7" }} />
          <span className="text-navy/60">{p.name}:</span>
          <span className="font-medium text-navy tabular-nums">
            {typeof p.value === "number" ? p.value.toLocaleString("es-MX") : p.value}
          </span>
        </div>
      ))}
    </div>
  );
}

function ChartBlock({ block }) {
  const data = block.data || [];
  const series = block.series || [];
  const x = block.x || "x";
  const axis = (
    <>
      <CartesianGrid strokeDasharray="4 8" stroke="rgba(14,26,43,0.05)" vertical={false} />
      <XAxis dataKey={x} tick={{ fontSize: 10.5, fill: "rgba(14,26,43,0.4)" }} tickLine={false} axisLine={false} dy={6} />
      <YAxis tick={{ fontSize: 10.5, fill: "rgba(14,26,43,0.4)" }} tickLine={false} axisLine={false}
        tickFormatter={(v) => (Math.abs(v) >= 1000000 ? `${(v / 1000000).toFixed(1)}M` : Math.abs(v) >= 1000 ? `${(v / 1000).toFixed(0)}k` : v)} width={42} />
      <Tooltip content={<GlassTooltip />} cursor={{ fill: "rgba(108,92,231,0.05)" }} />
    </>
  );
  let chart = null;
  if (block.chart === "pie") {
    const key = series[0]?.key || "value";
    const total = data.reduce((s, d) => s + (Number(d[key]) || 0), 0);
    chart = (
      <div className="flex flex-col sm:flex-row items-center gap-4">
        <div className="relative h-56 w-56 shrink-0">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Tooltip content={<GlassTooltip />} />
              <Pie data={data} dataKey={key} nameKey={x} innerRadius="66%" outerRadius="92%"
                paddingAngle={4} strokeWidth={0} cornerRadius={6}>
                {data.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
            <span className="text-2xl font-medium text-navy tabular-nums">{total.toLocaleString("es-MX")}</span>
            <span className="text-[10px] uppercase tracking-widest text-navy/40">Total</span>
          </div>
        </div>
        <div className="flex flex-wrap sm:flex-col gap-x-4 gap-y-1.5 text-sm">
          {data.map((d, i) => (
            <div key={i} className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: PALETTE[i % PALETTE.length] }} />
              <span className="text-navy/60 truncate max-w-[160px]">{String(d[x])}</span>
              <span className="ml-auto font-medium text-navy/80 tabular-nums">{Number(d[key]).toLocaleString("es-MX")}</span>
            </div>
          ))}
        </div>
      </div>
    );
    return (
      <motion.div data-testid="ai-block-chart" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease }} className="my-3 rounded-2xl border border-navy/10 bg-white p-5 shadow-sm">
        {block.title && (
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-3.5 h-3.5 text-[#6C5CE7]" />
            <span className="text-sm font-medium text-navy">{block.title}</span>
          </div>
        )}
        {chart}
      </motion.div>
    );
  } else if (block.chart === "area") {
    chart = (
      <AreaChart data={data} margin={{ top: 8, right: 8 }}>
        <defs>
          {series.map((s, i) => (
            <linearGradient key={s.key} id={`g-${s.key}-${i}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={s.color || PALETTE[i]} stopOpacity={0.35} />
              <stop offset="100%" stopColor={s.color || PALETTE[i]} stopOpacity={0} />
            </linearGradient>
          ))}
        </defs>
        {axis}
        {series.map((s, i) => (
          <Area key={s.key} type="monotone" dataKey={s.key} name={s.label || s.key}
            stroke={s.color || PALETTE[i]} strokeWidth={3} fill={`url(#g-${s.key}-${i})`}
            activeDot={{ r: 5, strokeWidth: 2, stroke: "#fff" }} />
        ))}
      </AreaChart>
    );
  } else if (block.chart === "line") {
    chart = (
      <LineChart data={data} margin={{ top: 8, right: 8 }}>
        {axis}
        {series.map((s, i) => (
          <Line key={s.key} type="monotone" dataKey={s.key} name={s.label || s.key}
            stroke={s.color || PALETTE[i]} strokeWidth={3} dot={false}
            activeDot={{ r: 5, strokeWidth: 2, stroke: "#fff" }} />
        ))}
      </LineChart>
    );
  } else {
    chart = (
      <BarChart data={data} margin={{ top: 8, right: 8 }} barCategoryGap="28%">
        <defs>
          {series.map((s, i) => (
            <linearGradient key={s.key} id={`bg-${s.key}-${i}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={s.color || PALETTE[i]} stopOpacity={0.95} />
              <stop offset="100%" stopColor={s.color || PALETTE[i]} stopOpacity={0.55} />
            </linearGradient>
          ))}
        </defs>
        {axis}
        {series.map((s, i) => (
          <Bar key={s.key} dataKey={s.key} name={s.label || s.key}
            fill={`url(#bg-${s.key}-${i})`} radius={[9, 9, 3, 3]} maxBarSize={40} />
        ))}
      </BarChart>
    );
  }
  return (
    <motion.div data-testid="ai-block-chart" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease }} className="my-3 rounded-2xl border border-navy/10 bg-white p-5 shadow-sm">
      {block.title && (
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="w-3.5 h-3.5 text-[#6C5CE7]" />
          <span className="text-sm font-medium text-navy">{block.title}</span>
        </div>
      )}
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">{chart}</ResponsiveContainer>
      </div>
    </motion.div>
  );
}

const LETTERS = "abcdefgh";

function OptionsBlock({ block, onSend, disabled }) {
  const [picked, setPicked] = useState(null);
  return (
    <motion.div data-testid="ai-block-options" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease }}
      className="my-3 rounded-2xl border border-[#6C5CE7]/25 bg-gradient-to-br from-[#6C5CE7]/[0.06] to-transparent p-5">
      <div className="text-sm font-medium text-navy mb-3">{block.question}</div>
      <div className="flex flex-col gap-2">
        {(block.options || []).map((o, i) => (
          <button key={i} data-testid={`ai-option-${i}`}
            disabled={disabled || picked !== null}
            onClick={() => { setPicked(i); onSend(o.label || o.value); }}
            className={`group text-left flex items-center gap-3 rounded-xl border px-4 py-2.5 text-sm transition-all
              ${picked === i ? "border-[#6C5CE7] bg-[#6C5CE7] text-white"
                : "border-navy/10 bg-white text-navy/80 hover:border-[#6C5CE7]/50 hover:translate-x-0.5"}
              ${picked !== null && picked !== i ? "opacity-40" : ""}`}>
            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-medium shrink-0
              ${picked === i ? "bg-white/20 text-white" : "bg-[#6C5CE7]/10 text-[#6C5CE7]"}`}>
              {LETTERS[i] || i + 1}
            </span>
            {o.label}
          </button>
        ))}
      </div>
    </motion.div>
  );
}

function ConfirmBlock({ block, onSend, disabled }) {
  const [answered, setAnswered] = useState(null);
  return (
    <motion.div data-testid="ai-block-confirm" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease }}
      className="my-3 rounded-2xl border border-[#C9A96A]/40 bg-gradient-to-br from-[#C9A96A]/10 to-transparent p-5">
      <div className="overline text-[#8a6d34] mb-1">{block.title || "Confirmar acción"}</div>
      <div className="text-sm text-navy/85">{block.summary}</div>
      {(block.details || []).length > 0 && (
        <div className="mt-3 space-y-1">
          {block.details.map((d, i) => (
            <div key={i} className="flex justify-between text-xs text-navy/60 border-b border-navy/5 pb-1">
              <span>{d.label}</span><span className="font-medium text-navy/80">{d.value}</span>
            </div>
          ))}
        </div>
      )}
      <div className="mt-4 flex gap-2">
        <button data-testid="ai-confirm-run" disabled={disabled || answered !== null}
          onClick={() => { setAnswered("yes"); onSend("Sí, confirmo. Ejecuta la acción."); }}
          className={`inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-medium transition-all
            ${answered === "yes" ? "bg-emerald-600 text-white" : "bg-navy text-cream hover:bg-navy-soft"} disabled:opacity-50`}>
          <Check className="w-4 h-4" /> Ejecutar
        </button>
        <button data-testid="ai-confirm-cancel" disabled={disabled || answered !== null}
          onClick={() => { setAnswered("no"); onSend("No, cancela esa acción."); }}
          className="inline-flex items-center gap-2 rounded-full border border-navy/15 px-5 py-2 text-sm text-navy/60 hover:text-navy transition-colors disabled:opacity-50">
          <X className="w-4 h-4" /> Cancelar
        </button>
      </div>
    </motion.div>
  );
}

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const absUrl = (u) => (u && u.startsWith("/") ? `${BACKEND}${u}` : u);

function ImageBlock({ block }) {
  const src = absUrl(block.url);
  return (
    <motion.div data-testid="ai-block-image" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease }} className="my-3 rounded-2xl border border-navy/10 bg-white p-3 shadow-sm max-w-md">
      <img src={src} alt={block.prompt || "Imagen generada por OMNIA"} className="rounded-xl w-full object-contain" />
      <div className="flex items-center justify-between mt-2 px-1">
        <span className="text-[11px] text-navy/40 truncate max-w-[70%]">{block.prompt}</span>
        <a href={src} target="_blank" rel="noreferrer" data-testid="ai-image-download"
          className="inline-flex items-center gap-1 text-[11px] text-[#6C5CE7] hover:underline">
          <Download className="w-3 h-3" /> Descargar
        </a>
      </div>
    </motion.div>
  );
}

function LinkBlock({ block }) {
  return (
    <motion.div data-testid="ai-block-link" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease }}
      className="my-3 rounded-2xl border border-[#6C5CE7]/25 bg-gradient-to-br from-[#6C5CE7]/[0.06] to-transparent p-5">
      <div className="text-sm font-medium text-navy">{block.title}</div>
      {block.description && <div className="mt-1 text-xs text-navy/50">{block.description}</div>}
      <a href={block.url} target="_blank" rel="noreferrer" data-testid="ai-link-open"
        className="mt-3 inline-flex items-center gap-2 rounded-full bg-navy text-cream px-5 py-2.5 text-sm font-medium hover:bg-[#6C5CE7] transition-colors">
        <ExternalLink className="w-4 h-4" /> {block.cta || "Abrir"}
      </a>
    </motion.div>
  );
}

function miniText(text) {
  return (text || "").split("\n").map((line, i) => {
    const html = line
      .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
      .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
    if (/^[-•·]\s/.test(line)) return <div key={i} className="flex gap-2 ml-1"><span className="opacity-50 mt-[1px]">·</span><span dangerouslySetInnerHTML={{ __html: html.replace(/^[-•·]\s/, "") }} /></div>;
    if (!line.trim()) return <div key={i} className="h-1.5" />;
    return <div key={i} dangerouslySetInnerHTML={{ __html: html }} />;
  });
}

function DirectorNoteBlock({ block }) {
  const d = block.director || {};
  const color = d.color || "#6C5CE7";
  return (
    <motion.div data-testid="ai-block-director-note" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease }}
      className="my-3 rounded-2xl border border-navy/10 bg-white p-4 shadow-sm"
      style={{ borderLeft: `3px solid ${color}` }}>
      <div className="flex items-center gap-2.5 mb-2">
        <span className="inline-flex items-center justify-center rounded-md px-2 py-0.5 text-[10px] font-semibold tracking-wider text-white"
          style={{ background: color }}>{d.short || "DIR"}</span>
        <span className="text-xs font-medium text-navy/70">{d.name || "Director"}</span>
      </div>
      <div className="text-[0.875rem] leading-relaxed text-navy/75 space-y-0.5">{miniText(block.text)}</div>
    </motion.div>
  );
}

function DecisionBlock({ block }) {
  const chips = [
    block.responsible && `Responsable: ${block.responsible}`,
    block.due_date && `Plazo: ${block.due_date}`,
  ].filter(Boolean);
  return (
    <motion.div data-testid="ai-block-decision" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease }}
      className="my-3 rounded-2xl border border-[#C9A96A]/40 bg-gradient-to-br from-[#C9A96A]/[0.12] to-transparent p-5">
      <div className="flex items-center gap-2">
        <Gavel className="w-4 h-4 text-[#8a6d34]" />
        <span className="text-[10px] uppercase tracking-[0.2em] text-[#8a6d34] font-medium">Decisión ejecutiva · Memoria corporativa</span>
      </div>
      {block.situation && <div className="mt-2.5 text-xs text-navy/50">{block.situation}</div>}
      <div className="mt-2 text-[0.95rem] font-medium text-navy leading-relaxed">{block.decision}</div>
      {block.plan && <div className="mt-2 text-[0.85rem] leading-relaxed text-navy/65 space-y-0.5">{miniText(block.plan)}</div>}
      {(chips.length > 0 || (block.kpis || []).length > 0) && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {chips.map((c, i) => (
            <span key={i} className="inline-flex items-center rounded-full bg-white border border-[#C9A96A]/35 px-3 py-1 text-[11px] text-[#8a6d34]">{c}</span>
          ))}
          {(block.kpis || []).map((k, i) => (
            <span key={`k${i}`} className="inline-flex items-center rounded-full bg-navy/5 border border-navy/10 px-3 py-1 text-[11px] text-navy/60">KPI · {k}</span>
          ))}
        </div>
      )}
    </motion.div>
  );
}

export function AiBlock({ block, onSend, disabled }) {
  if (!block) return null;
  switch (block.type) {
    case "kpis": return <KpisBlock block={block} />;
    case "table": return <TableBlock block={block} />;
    case "chart": return <ChartBlock block={block} />;
    case "options": return <OptionsBlock block={block} onSend={onSend} disabled={disabled} />;
    case "confirm": return <ConfirmBlock block={block} onSend={onSend} disabled={disabled} />;
    case "image": return <ImageBlock block={block} />;
    case "link": return <LinkBlock block={block} />;
    case "director_note": return <DirectorNoteBlock block={block} />;
    case "decision": return <DecisionBlock block={block} />;
    default: return null;
  }
}
